import React, { useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';

import api from '../services/api';
import { ProjectCostCollaboratorImportResponse } from '../types';

type Props = {
  show: boolean;
  onHide: () => void;
  onImported: () => Promise<void>;
  showAlert: (params: { title: string; message: string; variant?: 'success' | 'warning' | 'danger' | 'info' }) => void;
};

const ProjectCostCollaboratorImportModal: React.FC<Props> = ({
  show,
  onHide,
  onImported,
  showAlert,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [linkUsers, setLinkUsers] = useState(true);
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ProjectCostCollaboratorImportResponse | null>(null);

  const hasErrors = useMemo(() => Boolean(result?.errors?.length), [result]);

  const resetState = () => {
    setSelectedFile(null);
    setResult(null);
    setLoading(false);
    setDryRun(true);
    setLinkUsers(true);
  };

  const handleClose = () => {
    resetState();
    onHide();
  };

  const submitImport = async () => {
    if (!selectedFile) {
      showAlert({
        title: 'Arquivo obrigatório',
        message: 'Selecione uma planilha (.csv ou .xlsx) para importar.',
        variant: 'warning',
      });
      return;
    }

    const allowedExtensions = ['.csv', '.xlsx'];
    const lowerName = selectedFile.name.toLowerCase();
    if (!allowedExtensions.some((ext) => lowerName.endsWith(ext))) {
      showAlert({
        title: 'Tipo inválido',
        message: 'Tipos aceitos: .csv e .xlsx.',
        variant: 'warning',
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('dry_run', dryRun ? 'true' : 'false');
    formData.append('link_users', linkUsers ? 'true' : 'false');

    setLoading(true);
    try {
      const { data } = await api.post<ProjectCostCollaboratorImportResponse>(
        '/api/projects/costs/collaborators/import',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      setResult(data);

      if (!dryRun && (data.created > 0 || data.updated > 0)) {
        await onImported();
      }

      if (!dryRun) {
        showAlert({
          title: data.errors.length ? 'Importação concluída com pendências' : 'Importação concluída',
          message: data.errors.length
            ? `Importação aplicada parcialmente. Criados: ${data.created}. Atualizados: ${data.updated}. Erros: ${data.errors.length}.`
            : `Importação aplicada com sucesso. Criados: ${data.created}. Atualizados: ${data.updated}.`,
          variant: data.errors.length ? 'warning' : 'success',
        });
      }
    } catch (err: any) {
      showAlert({
        title: 'Erro ao importar',
        message: err?.response?.data?.detail || 'Não foi possível processar o arquivo de importação.',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Importar colaboradores por planilha</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="mb-3">
          Use colunas como <strong>nome</strong>, <strong>empresa</strong>, <strong>jira_account_id</strong>,{' '}
          <strong>jira_display_name</strong>, <strong>email</strong>, <strong>função</strong>, <strong>user_email</strong>{' '}
          e <strong>user_id</strong>. Também aceita formato de alocação com <strong>Lotação</strong>, <strong>Analista</strong>{' '}
          e <strong>Função</strong>. Campos de empresa/função aceitam nome ou ID.
        </Alert>
        <Form.Group className="mb-3">
          <Form.Label>Arquivo (.csv ou .xlsx)</Form.Label>
          <Form.Control
            type="file"
            accept=".csv,.xlsx"
            onChange={(event) => {
              const input = event.currentTarget as HTMLInputElement;
              setSelectedFile(input.files?.[0] || null);
            }}
            disabled={loading}
          />
        </Form.Group>
        <Form.Check
          id="import-collaborators-dry-run"
          className="mb-2"
          label="Executar simulação (não grava no banco)"
          checked={dryRun}
          onChange={(event) => setDryRun(event.target.checked)}
          disabled={loading}
        />
        <Form.Check
          id="import-collaborators-link-users"
          className="mb-3"
          label="Tentar vincular colaborador ao cadastro de usuário (user_id/user_email/email)"
          checked={linkUsers}
          onChange={(event) => setLinkUsers(event.target.checked)}
          disabled={loading}
        />

        {result && (
          <div className="border rounded p-3 bg-light">
            <div className="fw-semibold mb-2">Resultado</div>
            <div>Total de linhas: {result.total_rows}</div>
            <div>Criados: {result.created}</div>
            <div>Atualizados: {result.updated}</div>
            <div>Erros: {result.errors.length}</div>
            {result.dry_run && <div className="text-muted">Modo simulação: nenhuma alteração foi persistida.</div>}

            {hasErrors && (
              <div className="mt-3">
                <div className="fw-semibold mb-2">Erros encontrados</div>
                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                  <Table bordered hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Linha</th>
                        <th>Mensagem</th>
                        <th>Nome</th>
                        <th>E-mail</th>
                        <th>Jira accountId</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((item, idx) => (
                        <tr key={`${item.row_number}-${idx}`}>
                          <td>{item.row_number}</td>
                          <td>{item.message}</td>
                          <td>{item.name || '—'}</td>
                          <td>{item.email || '—'}</td>
                          <td>{item.jira_account_id || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose} disabled={loading}>
          Fechar
        </Button>
        <Button variant="primary" onClick={() => void submitImport()} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Processando...
            </>
          ) : dryRun ? (
            'Simular importação'
          ) : (
            'Importar e salvar'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProjectCostCollaboratorImportModal;
